// @flow
/**
 * A custom widget rendered on its own for a preview: its template filled with
 * the default props, its CSS scoped like on the page, and its script run once
 * the markup is there. Shown in iframes with sandbox="allow-scripts", where
 * the storage shim stands in for cookies and localStorage.
 */
import { scopeCss } from 'shared/lib/widgetCode/scopeCss';
import { scopeCssLegacyCustom } from 'shared/lib/widgetCode/legacyScopers';
import { PREVIEW_STORAGE_SHIM } from 'shared/lib/previewStorageShim';
import { wrapWidgetScriptV2 } from 'shared/lib/widgetScriptRuntime';
import { escapeClosingTag } from '~/lib/previewSandbox';

/**
 * Get nested value from object using dot notation
 */
function getNestedValue(obj, path) {
  const keys = path.split('.');
  let value = obj;
  for (const key of keys) {
    if (value === null || value === undefined) return undefined;
    value = value[key];
  }
  return value;
}

/**
 * Replace {{.}} only at the top level (not inside nested {{#...}}...{{/...}} blocks)
 */
function replaceTopLevelDot(template, item) {
  let replacement = '';
  if (typeof item !== 'object' || item === null) {
    replacement = String(item ?? '');
  } else {
    if ('value' in item) replacement = String(item.value ?? '');
    else if ('text' in item) replacement = String(item.text ?? '');
    else if ('name' in item) replacement = String(item.name ?? '');
    else if ('label' in item) replacement = String(item.label ?? '');
    else {
      const keys = Object.keys(item).filter(k => !k.startsWith('_'));
      if (keys.length === 1) replacement = String(item[keys[0]] ?? '');
    }
  }
  
  let depth = 0;
  let result = '';
  let i = 0;
  
  while (i < template.length) {
    if (template.slice(i, i + 3) === '{{#') {
      depth++;
      result += template[i];
      i++;
    } else if (template.slice(i, i + 3) === '{{/') {
      depth--;
      result += template[i];
      i++;
    } else if (depth === 0 && template.slice(i, i + 5) === '{{.}}') {
      result += replacement;
      i += 5;
    } else if (depth === 0 && template.slice(i).match(/^\{\{\s*\.\s*\}\}/)) {
      const match = template.slice(i).match(/^\{\{\s*\.\s*\}\}/);
      result += replacement;
      i += match[0].length;
    } else {
      result += template[i];
      i++;
    }
  }
  
  return result;
}

/**
 * Process array blocks in template (supports nested arrays)
 */
function processArrayBlocks(html, context) {
  const arrayBlockRegex = /\{\{#([a-zA-Z_][a-zA-Z0-9_]*)\}\}([\s\S]*?)\{\{\/\1\}\}/g;
  
  let result = html;
  let hasMatch = true;
  
  // Keep processing until no more array blocks are found (handles nested arrays)
  while (hasMatch) {
    hasMatch = false;
    result = result.replace(arrayBlockRegex, (match, arrayKey, template) => {
      hasMatch = true;
      const array = context[arrayKey];
      if (!Array.isArray(array) || array.length === 0) return '';
      
      return array.map((item, index) => {
        const itemContext = typeof item === 'object' && item !== null
          ? { ...item, _index: index, _first: index === 0, _last: index === array.length - 1 }
          : { _value: item, _index: index, _first: index === 0, _last: index === array.length - 1 };
        
        let itemResult = template;
        itemResult = replaceTopLevelDot(itemResult, item);
        itemResult = itemResult.replace(/\{\{\s*_index\s*\}\}/g, String(index));
        itemResult = itemResult.replace(/\{\{\s*_first\s*\}\}/g, String(itemContext._first));
        itemResult = itemResult.replace(/\{\{\s*_last\s*\}\}/g, String(itemContext._last));
        
        itemResult = itemResult.replace(/\{\{\s*([a-zA-Z_][a-zA-Z0-9_.]*)\s*\}\}/g, (m, path) => {
          if (path === '.' || path.startsWith('_') || path.startsWith('#') || path.startsWith('/')) return m;
          const value = getNestedValue(itemContext, path);
          if (value === null || value === undefined) return '';
          if (Array.isArray(value)) return m; // Keep for next iteration
          if (typeof value === 'object') return JSON.stringify(value);
          return String(value);
        });
        
        // Recursively process nested array blocks
        if (typeof item === 'object' && item !== null) {
          itemResult = processArrayBlocks(itemResult, itemContext);
        }
        
        return itemResult;
      }).join('');
    });
  }
  
  return result;
}

/**
 * Interpolate props into HTML
 */
export function interpolateProps(html, props) {
  if (!html || !props) return html;
  let result = processArrayBlocks(html, props);
  result = result.replace(/\{\{\s*([a-zA-Z_][a-zA-Z0-9_.]*)\s*\}\}/g, (match, path) => {
    const value = getNestedValue(props, path);
    if (value === null || value === undefined) return '';
    if (Array.isArray(value)) return '';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  });
  return result;
}

type VersionData = { html?: string, css?: string, js?: string, defaultProps?: Object, scoper?: number };

export function buildWidgetSrcDoc(versionData: VersionData, scopeClass: string, { padding = 12, dark = false, thumbnail = false }: { padding?: number, dark?: boolean, thumbnail?: boolean } = {}) {
  const html = versionData.html || '';
  const css = versionData.css || '';
  const js = versionData.js || '';
  const defaultProps = versionData.defaultProps || {};
  const interpolatedHtml = interpolateProps(html, defaultProps);
  // v2 widgets use the same scoper as on the page.
  const scopedCss = versionData.scoper === 2 ? scopeCss(css, scopeClass).css : scopeCssLegacyCustom(css, scopeClass);

  const isV2 = versionData.scoper === 2;
  // v2 widgets run with the same wrapper as on the page (root, scoped document, late load events).
  const v2Js = isV2 && js.trim() ? wrapWidgetScriptV2({
    js,
    rootSelector: '[data-widget-instance="preview"]',
    label: 'Custom widget',
    variables: [
      '  var instanceId = "preview";',
      '  var instanceSelector = \'[data-widget-instance="preview"]\';',
      `  var __WIDGET_PROPS__ = ${JSON.stringify(defaultProps)};`
    ].join('\n')
  }) : '';

  // Wrap JS in an IIFE with retry mechanism for DOM readiness
  const wrappedJs = isV2 ? v2Js : js.trim() ? `
    (function() {
      var scopeSelector = '.${scopeClass}';
      var __WIDGET_PROPS__ = ${JSON.stringify(defaultProps)};

      function executeScript() {
        var root = document.querySelector(scopeSelector);
        if (!root) {
          setTimeout(executeScript, 10);
          return;
        }
        try {
          ${js}
        } catch (error) {
          console.error('Widget script error:', error);
        }
      }

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', executeScript);
      } else {
        executeScript();
      }
    })();
  ` : '';

  return `
    <!DOCTYPE html>
    <html>
    <head>
      ${wrappedJs ? `<script>${PREVIEW_STORAGE_SHIM}</script>` : ''}
      <style>
        * { box-sizing: border-box; }
        body { margin: 0; padding: ${padding}px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 14px; ${dark ? 'background: #1a1a1a; color: #fff;' : 'background: #fff;'} }
        ${thumbnail ? 'html, body { overflow: hidden; }' : ''}
        ${escapeClosingTag(scopedCss, 'style')}
      </style>
    </head>
    <body>
      <div class="${scopeClass}" data-widget-instance="preview"><div>${interpolatedHtml}</div></div>
      ${wrappedJs ? `<script>${escapeClosingTag(wrappedJs, 'script')}</script>` : ''}
    </body>
    </html>
  `;
}
